import logger from '@/utils/logger';
import {
  buildOptions,
  getEndPoints
} from '../../../../utils/api-call';
import {
  makeCall
} from '../../../../utils/request';
import {
  SET_SURVEY_USER_VARIABLES,
  FETCH_SURVEY_PREVIEW_BY_UUID,
  CREATE_SURVEY_RESPONSE_REQUEST,
  SET_SPINNER_LOADING,
  SET_LOADING_REQUEST,
  SET_NEW_VERSION,
  SET_COMPLETED_COUNT,
  SET_SESSION_TOKEN
} from './constants';
import {
  percentSettings,
  sliderSettings
} from '../initialSurveyData';
import constantTypes from '../../../../views/questions/constants';
import _ from 'lodash';

export const state = {
  isLoadingRequest: false,
  isNewVersion: false,
  completedCount: null,
  surveyUserVariables: [],
  sessionToken: null
};

function buildSurveyDetailParams (data) {
  const params = Object.keys(data).map(function (key) {
    if (data[key] === null) {
      return;
    }
    return [key, data[key]].map(encodeURIComponent).join('=');
  }).join('&');
  const lastUnexpectedJoinIndex = params.lastIndexOf('&');
  if (lastUnexpectedJoinIndex > -1 && lastUnexpectedJoinIndex >= params.length - 1) {
    return params.substring(0, lastUnexpectedJoinIndex);
  }
  return params;
}

function standardSurvey (survey) {
  if (!survey || !survey.surveyQuestions) return;
  const questions = survey.surveyQuestions;
  if (survey.updatedNewVersion) {
    questions.updatedNewVersion = survey.updatedNewVersion;
  }
  const size = questions.length;
  for (let i = 0; i < size; i++) {
    const currentQuestion = questions[i];
    if (currentQuestion.groupQuestions) {
      for (let j = 0; j < currentQuestion.groupQuestions.length; j++) {
        const subQuestion = currentQuestion.groupQuestions[j];
        standardQuestion(subQuestion);
      }
    } else {
      standardQuestion(currentQuestion);
    }
  }
  survey.surveyQuestions = questions;
  return survey;
}

function standardQuestion (currentQuestion) {
  if (currentQuestion.surveyQuestionAnswers) {
    currentQuestion.surveyQuestionAnswers.forEach((o) => {
      o.title = o.answerTitle;
      o.text = o.answerText;
      if (o.defaultValue) {
        o.value = o.defaultValue;
      }

      if (o.unit) {
        o.questionUnit = o.unit;
      }
      if (typeof (o.numberDecimalPlace) !== 'undefined') {
        o.questionDecimal = o.numberDecimalPlace;
      }
      if (o.minValue >= 0) {
        o.minNumeric = o.minValue;
      }
      if (o.maxValue) {
        o.maxNumeric = o.maxValue;
      }

      if (o.maxLength) {
        currentQuestion.characters = o.maxLength;
      }
    });
  }
  if (currentQuestion.type === constantTypes.QUESTION_TYPES.SLIDER) {
    currentQuestion.settings = _.cloneDeep(sliderSettings);
    if (currentQuestion.surveyQuestionAnswers && currentQuestion.surveyQuestionAnswers.length > 0) {
      currentQuestion.rating = currentQuestion.surveyQuestionAnswers[0].defaultValue;
    }
  } else if (currentQuestion.type === constantTypes.QUESTION_TYPES.PERCENTAGE_SCALE) {
    currentQuestion.settings = _.cloneDeep(percentSettings);
    if (currentQuestion.surveyQuestionAnswers && currentQuestion.surveyQuestionAnswers.length > 0) {
      currentQuestion.rating = currentQuestion.surveyQuestionAnswers[0].defaultValue;
    }
  } else if (currentQuestion.type === constantTypes.QUESTION_TYPES.MULTIPLE_CHOICE_DATE_PICK) {
    currentQuestion.validDatePicker = true;
    if (currentQuestion.fixedRangeDateStatus) {
      if (currentQuestion.surveyQuestionResponseLogic && currentQuestion.surveyQuestionResponseLogic.indexOf('-') > -1) {
        const range = currentQuestion.surveyQuestionResponseLogic.split('-');
        currentQuestion.surveyQuestionResponseLogicResult = currentQuestion.surveyQuestionResponseLogic;
        currentQuestion.surveyQuestionResponseLogic = range[1];
      }
      currentQuestion.surveyQuestionAnswers.filter(answer => answer.answerResponse).forEach(a => {
        if (currentQuestion.fixedRangeDateStatus) {
          a.answerResponseResult = a.answerResponse;
          const rangeDates = a.answerResponse.split('-');
          if (rangeDates && rangeDates.length) {
            a.responseStartDate = rangeDates[0];
            a.responseEndDate = rangeDates[1];
            a.answerResponse = rangeDates[1];
          }
        } else if (a.answerResponse) {
          a.responseStartDate = a.answerResponse;
        }
      });
    }
  }
}

export const actions = {
  [SET_LOADING_REQUEST] ({ commit, state }, toggle) {
    state.isLoadingRequest = toggle;
  },
  [FETCH_SURVEY_PREVIEW_BY_UUID] ({ commit, state }, options) {
    if (state.isLoadingRequest) return;
    state.isLoadingRequest = true;
    return new Promise((resolve, reject) => {
      let url = getEndPoints().survey.detailByUuid
        .replace(':id', options.surveyUuid);
      delete options.surveyUuid;
      const fullUrl = url + buildSurveyDetailParams(options);
      makeCall(
        fullUrl,
        buildOptions('GET', undefined, undefined, { skipAuthen: true }),
        commit
      )
        .then(response => {
          if (response.doubleClick) {
            return resolve(null);
          }
          state.isLoadingRequest = false;
          const payload = response.data || response;
          commit(SET_NEW_VERSION, payload.updatedNewVersion);
          commit(SET_COMPLETED_COUNT, payload.completedCount);
          commit(SET_SURVEY_USER_VARIABLES, response.surveyUserVariables);
          // MT-2419: capture the time-bound session token issued with the public
          // survey preview so it can be forwarded on submit and survive reloads.
          const sessionToken = payload.sessionToken;
          if (sessionToken) {
            commit(SET_SESSION_TOKEN, sessionToken);
            try {
              sessionStorage.setItem('surveySessionToken', sessionToken);
            } catch (storageError) {
              logger.error('Unable to persist survey session token =', storageError);
            }
          }
          const survey = payload.survey;
          survey.progressUuid = response.progressUuid;
          resolve(standardSurvey(survey));
        }, error => {
          state.isLoadingRequest = false;
          reject(error);
        });
    });
  },
  [CREATE_SURVEY_RESPONSE_REQUEST] ({commit, state}, options) {
    if (state.isLoadingRequest) return;
    state.isLoadingRequest = true;
    // MT-2419: forward the session token (state, falling back to sessionStorage)
    // as a custom header. Only attach it when present so other flows are unaffected.
    let sessionToken = state.sessionToken;
    if (!sessionToken) {
      try {
        sessionToken = sessionStorage.getItem('surveySessionToken');
        if (sessionToken) {
          commit(SET_SESSION_TOKEN, sessionToken);
        }
      } catch (storageError) {
        sessionToken = null;
      }
    }
    const customHeaders = sessionToken
      ? { 'X-Survey-Session-Token': sessionToken }
      : undefined;
    return new Promise((resolve, reject) => {
      makeCall(
        getEndPoints().survey.responses.submit,
        buildOptions('POST', options.data, customHeaders, { skipAuthen: true }), commit)
        .then(response => {
          if (response.doubleClick) {
            return resolve(null);
          }
          state.isLoadingRequest = false;
          const survey = response.survey;
          resolve(standardSurvey(survey));
        }, error => {
          logger.error('error =', error);
          state.isLoadingRequest = false;
          reject(error);
        });
    });
  }
};

export const getters = {
  getNewVersion: state => state.isNewVersion,
  getCompletedCount: state => state.completedCount,
  getSurveyUserVariables: state => state.surveyUserVariables
};

export const mutations = {
  [SET_SURVEY_USER_VARIABLES] (currentState, surveyUserVariables) {
    currentState.surveyUserVariables = surveyUserVariables;
  },
  [SET_SPINNER_LOADING] (currentState, toggle) {
    currentState.isLoading = toggle;
  },
  [SET_NEW_VERSION] (currentState, data) {
    currentState.isNewVersion = data;
  },
  [SET_COMPLETED_COUNT] (currentState, data) {
    currentState.completedCount = data;
  },
  [SET_SESSION_TOKEN] (currentState, sessionToken) {
    currentState.sessionToken = sessionToken;
  }
};

export default {
  state,
  actions,
  getters,
  mutations
};
